import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Home, Download, Check, X, Handshake, Clock, Loader2 } from "lucide-react";
import { API } from "./api";

const fmt = (n) => { try { return `${parseInt(n).toLocaleString("fr-FR")} €`; } catch { return n; } };
const dt = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const inp = "w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white outline-none focus:border-[#C19A6B]";

export default function BuyerOffer() {
  const { id } = useParams();
  const [o, setO] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);
  const [f, setF] = useState({ amount: "", validity_date: "", conditions: "" });
  const [msg, setMsg] = useState("");

  const load = () => fetch(`${API}/offers/${id}`).then((r) => (r.ok ? r.json() : null)).then(setO).catch(() => setO(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (o === undefined) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#C19A6B] border-t-transparent rounded-full animate-spin" /></div>;
  if (o === null) return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center"><p className="text-zinc-400">Offre introuvable.</p></div>;

  const nego = o.negotiation || [];
  const last = nego[nego.length - 1] || o.offer || {};
  const counterPending = o.status === "counter_sent";
  const closed = ["accepted", "refused", "expired"].includes(o.status);

  const act = async (action, extra = {}) => {
    setBusy(true); setMsg("");
    try {
      const res = await fetch(`${API}/offers/${id}/buyer-action`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action, ...extra }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Erreur");
      setO(await res.json()); setMode(null); setF({ amount: "", validity_date: "", conditions: "" });
    } catch (e) { setMsg(e.message || "Action impossible"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-20 pb-20 px-6" data-testid="buyer-offer">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 text-[#C19A6B] mb-2"><Home className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.25em]">Suivi de mon offre</span></div>
        <h1 className="text-3xl font-light mb-1">Offre {o.ref}</h1>
        <p className="text-sm text-zinc-400 mb-6">{o.listing_title} — {o.listing_city}</p>

        <div className="border border-white/10 p-5 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider text-[#C19A6B]">{counterPending ? "Contre-proposition du vendeur" : "Ma proposition actuelle"}</span>
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-white/15 text-zinc-300" data-testid="buyer-status">{o.expired ? "Expirée" : o.status_label}</span>
          </div>
          <div className="text-4xl font-light text-white mb-2">{fmt(last.amount)}</div>
          {last.validity_date && <div className="text-sm text-zinc-400">Validité : {last.validity_date}</div>}
          {last.conditions && <div className="text-sm text-zinc-400">Conditions : {last.conditions}</div>}
        </div>

        <a href={`${API}/offers/${id}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="buyer-pdf" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider border border-white/15 text-zinc-300 px-4 py-2.5 hover:border-[#C19A6B] hover:text-[#C19A6B] mb-6"><Download className="w-4 h-4" /> Télécharger mon offre</a>

        {msg && <p className="text-sm text-red-400 mb-4">{msg}</p>}

        {counterPending && !closed && (
          <div className="space-y-3" data-testid="buyer-counter-actions">
            {!mode && (
              <div className="grid sm:grid-cols-3 gap-2">
                <button onClick={() => act("accept_counter")} disabled={busy} data-testid="buyer-accept-counter" className="inline-flex items-center justify-center gap-2 bg-emerald-500/90 hover:bg-emerald-500 text-black uppercase tracking-wider text-sm py-3">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Accepter</button>
                <button onClick={() => setMode("new")} data-testid="buyer-newoffer-btn" className="inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white uppercase tracking-wider text-sm py-3"><Handshake className="w-4 h-4" /> Nouvelle offre</button>
                <button onClick={() => act("refuse_counter")} disabled={busy} data-testid="buyer-refuse-counter" className="inline-flex items-center justify-center gap-2 border border-red-400/40 text-red-300 uppercase tracking-wider text-sm py-3 hover:bg-red-500/10"><X className="w-4 h-4" /> Refuser</button>
              </div>
            )}
            {mode === "new" && (
              <div className="border border-white/10 p-4 space-y-3" data-testid="buyer-newoffer-form">
                <input type="number" placeholder="Nouveau montant (€)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={inp} data-testid="buyer-newoffer-amount" />
                <input type="date" value={f.validity_date} onChange={(e) => setF({ ...f, validity_date: e.target.value })} className={inp} />
                <textarea rows={2} placeholder="Conditions" value={f.conditions} onChange={(e) => setF({ ...f, conditions: e.target.value })} className={inp} />
                <div className="flex gap-2">
                  <button onClick={() => f.amount && act("new_offer", f)} disabled={busy || !f.amount} data-testid="buyer-newoffer-submit" className="bg-[#C19A6B] hover:bg-[#d1af84] text-white uppercase tracking-wider text-sm px-4 py-2.5">Envoyer au conseiller</button>
                  <button onClick={() => setMode(null)} className="border border-white/15 text-zinc-400 uppercase tracking-wider text-sm px-4 py-2.5">Annuler</button>
                </div>
              </div>
            )}
          </div>
        )}
        {closed && <div className={`border p-4 text-center ${o.status === "accepted" ? "border-emerald-400/40 bg-emerald-500/5" : "border-white/10"}`}><p className="text-sm text-zinc-300">Statut final : <b className="text-[#C19A6B]">{o.status_label}</b></p></div>}
        {!counterPending && !closed && <p className="text-sm text-zinc-500">Votre offre suit son cours. Vous serez notifié à chaque étape par e-mail.</p>}

        <div className="border border-white/10 p-4 mt-6">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Historique</p>
          <div className="space-y-1.5">
            {(o.history || []).map((h, i) => (
              <div key={i} className="flex items-start gap-2 text-xs"><Clock className="w-3 h-3 text-zinc-600 mt-0.5" /><div><span className="text-zinc-300">{h.note}</span><span className="text-zinc-600 ml-2">{dt(h.at)}</span></div></div>
            ))}
          </div>
        </div>
        <Link to={`/bien/${o.listing_slug}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 hover:text-[#C19A6B] mt-4">Voir l'annonce</Link>
      </div>
    </div>
  );
}
